(() => {
  if (customElements.get('product-freight-quote-modal')) return;

  const CHECKBOX_FIELDS = [
    'contact[Forklift Available]',
    'contact[Loading Dock Available]',
    'contact[Tailgate Truck Required]'
  ];

  const yn = (checked) => (checked ? 'Yes' : 'No');

  class ProductFreightQuoteModal extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.overlay = this.querySelector('[data-freight-quote-overlay]');
      this.closeBtn = this.querySelector('[data-freight-quote-close]');
      this.form = this.querySelector('.freight-quote__form');
      this.success = this.querySelector('[data-freight-quote-success]');
      this.bodyField = this.querySelector('[data-freight-quote-body]');
      this.subjectField = this.querySelector('[data-freight-quote-subject]');
      this.submitBtn = this.form
        ? this.form.querySelector('[type="submit"]')
        : null;
      this._submitLabel = this.submitBtn ? this.submitBtn.textContent : '';
      this.skuInput = this.querySelector('[data-freight-quote-sku]');
      this.urlInput = this.querySelector('[data-freight-quote-url]');
      this.priceInput = this.querySelector('[data-freight-quote-price]');
      this.qtySelectedInput = this.querySelector('[data-freight-quote-qty-selected]');
      this.qtyInput = this.querySelector('[data-freight-quote-qty]');
      this.productTitle = this.dataset.productTitle || '';
      this.variantSelect = document.querySelector('[data-newegg-buybox-variant-id]');

      this.variants = [];
      this.variantById = new Map();
      const variantsEl = this.querySelector('[data-freight-quote-variants]');
      if (variantsEl) {
        try {
          this.variants = JSON.parse(variantsEl.textContent);
        } catch (e) {
          this.variants = [];
        }
        this.variantById = new Map(this.variants.map((v) => [v.id, v]));
      }

      this._onTrigger = this._onTrigger.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);

      document.addEventListener('click', this._onTrigger);
      document.addEventListener('newegg:variant-change', this._onVariantChange);
      if (this.overlay) this.overlay.addEventListener('click', () => this.setOpen(false));
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.setOpen(false));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.setOpen(false);
      });
      if (this.form) this.form.addEventListener('submit', this._onSubmit);
      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', () => this._syncQtySelected());
      }

      this.syncVariant();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onTrigger);
      document.removeEventListener('newegg:variant-change', this._onVariantChange);
      if (this.classList.contains('is-open')) document.body.classList.remove('freight-quote-open');
    }

    _onTrigger(e) {
      const btn = e.target.closest('[data-freight-quote-open]');
      if (!btn) return;
      e.preventDefault();
      this.setOpen(true);
    }

    _onVariantChange() {
      this.syncVariant();
    }

    syncVariant() {
      if (!this.variantSelect || this.variantById.size === 0) return;
      const id = parseInt(this.variantSelect.value, 10);
      const match = this.variantById.get(id);
      if (!match) return;
      if (this.skuInput) this.skuInput.value = match.sku || '';
      if (this.priceInput) this.priceInput.value = match.price || '';
      if (this.urlInput) {
        this.urlInput.value = `${this.urlInput.dataset.base || ''}?variant=${id}`;
      }
    }

    _syncQtySelected() {
      if (!this.qtyInput || !this.qtySelectedInput) return;
      this.qtySelectedInput.value = this.qtyInput.value || '1';
    }

    setOpen(open) {
      if (open) {
        this.syncVariant();
        if (this.form) {
          this.form.hidden = false;
          this.form.reset();
          if (this.qtyInput && !this.qtyInput.value) this.qtyInput.value = '1';
          this._syncQtySelected();
        }
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
          if (this._submitLabel) this.submitBtn.textContent = this._submitLabel;
        }
        if (this.success) this.success.hidden = true;
        if (this.subjectField && this.productTitle) {
          this.subjectField.value = `Freight Quote Request – ${this.productTitle}`;
        }
      }

      this.classList.toggle('is-open', open);
      document.body.classList.toggle('freight-quote-open', open);
      if (this.overlay) this.overlay.hidden = !open;
    }

    _fieldValue(name) {
      if (!this.form) return '';
      const el = this.form.querySelector(`[name="${name}"]`);
      if (!el) return '';
      if (el.type === 'checkbox') return yn(el.checked);
      if (el.type === 'radio') {
        const checked = this.form.querySelector(`[name="${name}"]:checked`);
        return checked ? checked.value : '';
      }
      return el.value.trim();
    }

    _buildBody() {
      const suburb = this._fieldValue('contact[Suburb]');
      const postcode = this._fieldValue('contact[Postcode]');
      const state = this._fieldValue('contact[State]');
      const deliveryLocation = [suburb, state, postcode].filter(Boolean).join(' ');

      const lines = [
        'Freight Quote Request',
        '',
        'Product:',
        this._fieldValue('contact[Product Title]') || this.productTitle,
        '',
        'SKU:',
        this._fieldValue('contact[SKU]'),
        '',
        'Product URL:',
        this._fieldValue('contact[Product URL]'),
        '',
        'Product Price:',
        this._fieldValue('contact[Product Price]'),
        '',
        'Quantity:',
        this._fieldValue('contact[Quantity required]'),
        '',
        'Customer:',
        this._fieldValue('contact[Full Name]'),
        '',
        'Company:',
        this._fieldValue('contact[Company Name]') || '—',
        '',
        'Phone:',
        this._fieldValue('contact[Phone]'),
        '',
        'Email:',
        this._fieldValue('contact[email]'),
        '',
        'Delivery Location:',
        deliveryLocation,
        '',
        'Address Type:',
        this._fieldValue('contact[Delivery Address Type]'),
        '',
        'Forklift Available:',
        this._fieldValue('contact[Forklift Available]'),
        '',
        'Loading Dock Available:',
        this._fieldValue('contact[Loading Dock Available]'),
        '',
        'Tailgate Required:',
        this._fieldValue('contact[Tailgate Truck Required]'),
        '',
        'Installation Location:',
        this._fieldValue('contact[Installation Location]'),
        '',
        'Required By:',
        this._fieldValue('contact[Required By Date]') || '—',
        '',
        'Comments:',
        this._fieldValue('contact[Additional Comments]') || '—',
        '',
        'Product Vendor:',
        this._fieldValue('contact[Product Vendor]')
      ];

      CHECKBOX_FIELDS.forEach((name) => {
        const el = this.form.querySelector(`[name="${name}"]`);
        if (el && !el.checked) el.disabled = true;
      });

      return lines.join('\n');
    }

    _onSubmit(e) {
      e.preventDefault();
      if (!this.form) return;

      this._syncQtySelected();

      const submitBtn = this.form.querySelector('[type="submit"]');
      const api = window.TpuStorefrontEnquiry;
      if (!api || typeof api.runEnquirySubmit !== 'function') {
        if (window.ModalToast) {
          window.ModalToast.show(
            "Thanks — we've received your enquiry and will be in touch shortly.",
          );
        }
        this.setOpen(false);
        if (this.form) this.form.reset();
        return;
      }

      const fv = (name) => api.fieldValue(this.form, name);
      api
        .runEnquirySubmit({
          button: submitBtn,
          successEl: this.success,
          payload: {
            enquiryType: 'freight',
            enquiryTypeLabel: fv('contact[Enquiry type]') || 'Freight Quote Request',
            subject: fv('contact[subject]') || undefined,
            name: fv('contact[Full Name]'),
            businessName: fv('contact[Company Name]'),
            email: fv('contact[email]'),
            phone: fv('contact[Phone]'),
            product: fv('contact[Product Title]') || this.productTitle,
            sku: fv('contact[SKU]'),
            productUrl: fv('contact[Product URL]'),
            productPrice: fv('contact[Product Price]'),
            vendor: fv('contact[Product Vendor]'),
            quantity: fv('contact[Quantity required]'),
            deliveryPostcode: fv('contact[Postcode]'),
            suburb: fv('contact[Suburb]'),
            state: fv('contact[State]'),
            requiredByDate: fv('contact[Required By Date]'),
            comments: fv('contact[Additional Comments]'),
            addressType: fv('contact[Delivery Address Type]'),
            forkliftAvailable: fv('contact[Forklift Available]'),
            loadingDockAvailable: fv('contact[Loading Dock Available]'),
            tailgateRequired: fv('contact[Tailgate Truck Required]'),
            installationLocation: fv('contact[Installation Location]'),
            acknowledged: (() => {
              const el = this.form.querySelector(
                '[name="contact[Freight acknowledgement]"]',
              );
              if (!el) return '';
              if (el.type === 'checkbox') return el.checked ? 'Yes' : 'No';
              return String(el.value || '').trim();
            })(),
          },
          onSuccess: () => {
            this.setOpen(false);
            if (this.form) {
              this.form.hidden = false;
              this.form.reset();
            }
            if (this.success) this.success.hidden = true;
          },
        })
        .catch(() => {});
    }
  }

  customElements.define('product-freight-quote-modal', ProductFreightQuoteModal);
})();
